import { ExceptionReason } from "@api/graphql/generated/generated-types";
import {
  buildReattemptLadder,
  getExceptionReasonFinalStateLabel,
  ReattemptLadderRow,
} from "@src/exceptionReasons/utils/exceptionReasonLabels";

interface ForceAttemptConsequencePanelProps {
  exceptionReason: ExceptionReason | undefined;
}

export function ForceAttemptConsequencePanel({
  exceptionReason,
}: ForceAttemptConsequencePanelProps) {
  if (!exceptionReason)
    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Select an exception reason to see what it will do.
        </p>
      </div>
    );

  const ladderRows: ReattemptLadderRow[] = buildReattemptLadder(
    exceptionReason.reattemptConfigs
  );
  const finalStateLabel: string = getExceptionReasonFinalStateLabel(
    exceptionReason.finalState
  );

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/20">
      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
        How this reason escalates
      </p>

      {ladderRows.length === 0 ? (
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
          {`No re-attempts. Recording this immediately marks the order ${finalStateLabel}.`}
        </p>
      ) : (
        <ul className="mt-2 space-y-1">
          {ladderRows.map((row) => (
            <li
              key={row.step}
              className="flex flex-col text-sm text-gray-700 dark:text-gray-300 sm:flex-row sm:gap-2"
            >
              <span className="shrink-0 text-gray-500 dark:text-gray-400">
                {row.step}
              </span>
              <span>{`${row.timing} · POD ${row.pod} · ${row.driverAssignment}`}</span>
            </li>
          ))}
          <li className="flex flex-col text-sm sm:flex-row sm:gap-2">
            <span className="shrink-0 text-gray-500 dark:text-gray-400">
              Then
            </span>
            <span className="font-semibold text-red-600 dark:text-red-400">
              {`Order ${finalStateLabel}`}
            </span>
          </li>
        </ul>
      )}

      <p className="mt-3 border-t border-amber-300 pt-2 text-xs text-gray-600 dark:border-amber-700 dark:text-gray-400">
        Prior attempts on this stop are not shown here, so this may be the final
        one. The final step applies to the whole order, not just this stop.
      </p>
    </div>
  );
}
